import { useState, useCallback } from 'react';
import '../../../hooks/useScrollCapture/supportsPassiveEvents.js';
import 'react/jsx-runtime';
import 'react-highlight-words';
import useThrottle from '../../../hooks/useThrottle.js';
import useKey from '../../../hooks/useKey.js';

const getTableBodyElement = () => {
    const selector = '.MuiTableBody-root';
    const element = document.querySelector(selector);
    if (!element || !element.childNodes.length) {
        return undefined;
    }
    return element.childNodes[0];
};
const getRowElement = (rowIndex) => {
    const selector = `.data-table-row-${rowIndex}`;
    return document.querySelector(selector);
};
const useKeyboardRowNavigation = ({ clickedRowId, enableRowClickSelection, rows, onRowClick, onRowDoubleClick, setRowClicked, resetRowClicked, totalHeight, rowHeight, enableRowClickKeyboardNavigation, }) => {
    const [scrollProps, setScrollProps] = useState();
    const onScroll = useCallback((props) => {
        setScrollProps(props);
    }, []);
    const getNextRowIndex = useCallback((factor) => {
        const rowIndex = rows.findIndex((row) => row.id === clickedRowId);
        if (rowIndex < 0) {
            return -1;
        }
        const nextIndex = rowIndex + factor;
        return nextIndex >= rows.length || nextIndex < 0 ? -1 : nextIndex;
    }, [clickedRowId, rows]);
    const onKeyboardNavigate = useCallback(factor => {
        if (clickedRowId && enableRowClickSelection && scrollProps && enableRowClickKeyboardNavigation) {
            const nextIndex = getNextRowIndex(factor);
            if (nextIndex < 0) {
                return;
            }
            const rowElement = getRowElement(nextIndex);
            if (!rowElement) {
                return;
            }
            const body = getTableBodyElement();
            if (!body) {
                return;
            }
            const { scrollTop } = body;
            const offset = totalHeight + scrollProps.scrollOffset;
            const top = parseInt(rowElement.style.top.replace('px', ''), 0);
            if (top + rowHeight >= offset) {
                body.scrollTo({ top: scrollTop + rowHeight });
            }
            else if (top <= scrollTop) {
                body.scrollTo({ top: scrollTop - rowHeight });
            }
            const nextRow = rows[nextIndex];
            onRowClick && onRowClick(nextRow);
            setRowClicked(nextRow.id);
        }
    }, [
        clickedRowId,
        enableRowClickSelection,
        scrollProps,
        enableRowClickKeyboardNavigation,
        getNextRowIndex,
        totalHeight,
        rowHeight,
        rows,
        onRowClick,
        setRowClicked,
    ]);
    const clickOnRow = useCallback(() => {
        if (clickedRowId && enableRowClickSelection && enableRowClickKeyboardNavigation) {
            const row = rows.find(({ id }) => id.toString() === clickedRowId.toString());
            if (row) {
                onRowDoubleClick && onRowDoubleClick(row);
            }
        }
    }, [clickedRowId, enableRowClickSelection, enableRowClickKeyboardNavigation, rows, onRowDoubleClick]);
    const clearRowClick = useCallback(() => {
        if (clickedRowId && enableRowClickSelection && enableRowClickKeyboardNavigation) {
            const row = rows.find(({ id }) => id.toString() === clickedRowId.toString());
            if (row) {
                onRowClick && onRowClick(undefined);
                resetRowClicked();
            }
        }
    }, [clickedRowId, enableRowClickSelection, enableRowClickKeyboardNavigation, rows, onRowClick, resetRowClicked]);
    const navigate = useThrottle(onKeyboardNavigate, 100);
    useKey('ArrowUp', () => navigate(-1), {}, [clickedRowId, enableRowClickKeyboardNavigation]);
    useKey('ArrowDown', () => navigate(1), {}, [clickedRowId, enableRowClickKeyboardNavigation]);
    useKey((event) => event.keyCode === 9, () => navigate(1), {}, [clickedRowId, enableRowClickKeyboardNavigation]);
    useKey('Enter', () => clickOnRow(), {}, [clickedRowId, enableRowClickKeyboardNavigation]);
    useKey((event) => event.keyCode === 27, () => clearRowClick(), {}, [clickedRowId, enableRowClickKeyboardNavigation]);
    return {
        onScroll,
    };
};

export { useKeyboardRowNavigation as default };
//# sourceMappingURL=useKeyboardRowNavigation.js.map
